import { theme } from 'antd';

import type { CustomDesignToken } from '@common/types/custom-design-token';

import type { ThemeConfig } from 'antd';
import type { SeedToken, MapToken, AliasToken } from 'antd/es/theme/interface';

const { getDesignToken } = theme;

// https://ant.design/docs/react/customize-theme#theme-editor
const darkModeDesignTokenOverrides: Partial<AliasToken> = {
  colorBgBase: '#121214',
  colorBgLayout: '#1e1e24',
  colorTextBase: '#fbfbfc',

  colorPrimary: '#3388ff',
  colorPrimaryBg: '#233061',
  colorPrimaryBgHover: '#223d7a',
  colorPrimaryBorder: '#1f4399',
  colorPrimaryBorderHover: '#2558b8',
  colorPrimaryHover: '#3072db',
  colorPrimaryActive: '#6eabfa',
  colorPrimaryText: '#b3d7fc',
  colorPrimaryTextHover: '#90c2fc',
  colorPrimaryTextActive: '#d9ebfc',

  colorSuccess: '#1cb854',
  colorSuccessBg: '#1f473d',
  colorSuccessBgHover: '#195848',
  colorSuccessBorder: '#136a4d',
  colorSuccessBorderHover: '#0e8450',
  colorSuccessHover: '#149e53',
  colorSuccessActive: '#4dd470',
  colorSuccessTextHover: '#72e985',
  colorSuccessText: '#a3f7a9',
  colorSuccessTextActive: '#d2fbd0',

  colorWarning: '#ffa02e',
  colorWarningBg: '#472e1f',
  colorWarningBgHover: '#7a4222',
  colorWarningBorder: '#934c1a',
  colorWarningBorderHover: '#b75f17',
  colorWarningHover: '#db7e21',
  colorWarningActive: '#ffbe62',
  colorWarningText: '#ffe4ab',
  colorWarningTextHover: '#ffd181',
  colorWarningTextActive: '#fff0cc',

  colorError: '#f9316d',
  colorErrorBg: '#472033',
  colorErrorBgHover: '#77214c',
  colorErrorBorder: '#901d56',
  colorErrorBorderHover: '#b3185e',
  colorErrorHover: '#d6236a',
  colorErrorActive: '#fb6382',
  colorErrorText: '#feacad',
  colorErrorTextHover: '#fd838f',
  colorErrorTextActive: '#fed9d5',

  colorInfo: '#3388ff',
  colorInfoBg: '#233061',
  colorInfoBgHover: '#223d7a',
  colorInfoBorder: '#1f4399',
  colorInfoBorderHover: '#2558b8',
  colorInfoHover: '#3072db',
  colorInfoActive: '#6eabfa',
  colorInfoText: '#b3d7fc',
  colorInfoTextHover: '#90c2fc',
  colorInfoTextActive: '#d9ebfc',

  colorLink: '#6eabfa',
  colorLinkHover: '#90c2fc',
  colorLinkActive: '#3072db',
};

const lightModeDesignTokenOverrides: Partial<AliasToken> = {
  colorBgBase: '#ffffff',
  colorTextBase: '#1a1c21',

  colorPrimary: '#1f7cff',
  colorSuccess: '#1cb854',
  colorWarning: '#ffa02e',
  colorError: '#f9316d',
};

export const customDarkModeDesignToken: CustomDesignToken = {
  colorFillAlterSolid: '#2d2d38',
  colorMethodDelete: '#fa82a2',
  colorMethodGet: '#68cc97',
  colorMethodPost: '#90b0f0',
  colorMethodPut: '#e0a663',
  colorMethodPatch: '#e0c363',
  colorMethodDefault: '#b3a6f9',
};

export const customLightModeDesignToken: CustomDesignToken = {
  colorFillAlterSolid: '#fafafa',
  colorMethodDelete: '#e70b46',
  colorMethodGet: '#25b869',
  colorMethodPost: '#1e65f5',
  colorMethodPut: '#f5901d',
  colorMethodPatch: '#f5c31d',
  colorMethodDefault: '#7a69ee',
};

export const darkModeThemeConfig: ThemeConfig = {
  algorithm: withoutBgBaseColor(theme.darkAlgorithm),
  token: darkModeDesignTokenOverrides,
};

export const lightModeThemeConfig: ThemeConfig = {
  algorithm: withoutBgBaseColor(theme.defaultAlgorithm),
  token: lightModeDesignTokenOverrides,
};

export const darkModeDesignToken: AliasToken & CustomDesignToken = {
  ...getDesignToken(darkModeThemeConfig),
  ...customDarkModeDesignToken,
};

export const lightModeDesignToken: AliasToken & CustomDesignToken = {
  ...getDesignToken(lightModeThemeConfig),
  ...customLightModeDesignToken,
};

// https://github.com/ant-design/ant-design/issues/44006#issuecomment-2773461322
function withoutBgBaseColor(derivative: (token: SeedToken) => MapToken) {
  return (token: SeedToken) => {
    const mapToken = derivative(token);

    mapToken.colorPrimary = token.colorPrimary;
    mapToken.colorSuccess = token.colorSuccess;
    mapToken.colorWarning = token.colorWarning;
    mapToken.colorError = token.colorError;
    mapToken.colorInfo = token.colorInfo;
    mapToken.colorLink = token.colorLink;

    return mapToken;
  };
}
